import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { UsernameStorageService } from '../../../services/username-storage.service';
import { GameSocketService } from '../../../services/socket.service';
import { RoomChromeService } from '../../../services/room-chrome.service';
import { SoundControlComponent } from '../sound-control/sound-control.component';
import {
  SessionConflictComponent,
  SessionConflictKind,
} from '../session-conflict/session-conflict.component';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'snk-main-layout',
  // NOTE: keep heavy libs (e.g. lucide-angular) out of this component — it is
  // eagerly loaded, so anything imported here lands in the initial bundle.
  imports: [
    RouterOutlet,
    RouterLink,
    SoundControlComponent,
    SessionConflictComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  private usernameStorage = inject(UsernameStorageService);
  private socketService = inject(GameSocketService);
  private roomChrome = inject(RoomChromeService);
  private router = inject(Router);
  private toastService = inject(ToastService);

  /** Signal-backed so the header reflects a rename without a reload. */
  readonly username = this.usernameStorage.username;

  /** Room identity published by the room page; null outside a room. */
  readonly roomInfo = this.roomChrome.roomInfo;

  readonly connectionStatus = toSignal(this.socketService.connectionStatus$, {
    initialValue: { connected: false, connecting: false, error: '' },
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /** True while viewing a game room — shows the controls hint in the footer. */
  readonly isRoomRoute = computed(() =>
    this.currentUrl().startsWith('/room/')
  );

  /** Set while this player's session is live in another tab. */
  readonly sessionConflict = computed<SessionConflictKind | null>(() => {
    const state = this.socketService.sessionState();
    return state === 'conflict' || state === 'replaced' ? state : null;
  });
  readonly isLobbyRoute = computed(() => this.currentUrl().split('?')[0] === '/');
  readonly takingOver = signal(false);

  /** Move the session to this tab, then into its room if it has one. */
  async playHere(): Promise<void> {
    if (this.takingOver()) return;
    this.takingOver.set(true);
    try {
      const welcome = await this.socketService.takeOver();
      const room = welcome?.activeRoom;
      // The room page re-requests its room on its own; elsewhere, go to it.
      if (room && !this.currentUrl().startsWith(`/room/${room.joinCode}`)) {
        await this.router.navigate(['/room', room.joinCode]);
      }
    } catch {
      this.toastService.error('Could not reach the server. Try again.');
    } finally {
      this.takingOver.set(false);
    }
  }

  backToLobby(): void {
    void this.router.navigate(['/']);
  }

  copyRoomLink(): void {
    void this.roomChrome.copyRoomLink();
  }

  requestLeave(): void {
    this.roomChrome.requestLeave();
  }
}
